// Copyright 2026 TokenLat. Licensed under the Apache License, Version 2.0.
import { useRef, useState } from 'react';

import { t } from './tokenlat-ui';
import { type AccountOptions } from './tokenlat-api';

import styles from './tokenlat-account.module.less';

export function GroupLabel({
  value,
  options,
}: {
  value: string;
  options: AccountOptions;
}) {
  const ratio = options.group_ratios[value];
  return (
    <span className={styles['group-label']}>
      <span>
        <strong>{value || '—'}</strong>
        <span className={styles['group-description']}>
          {options.groups[value] || value}
        </span>
      </span>
      {ratio !== undefined || value === 'auto' ? (
        <span className={styles.ratio}>
          {ratio ?? t('auto_ratio')}× {t('ratio')}
        </span>
      ) : null}
    </span>
  );
}

export function GroupSelector({
  options,
  value,
  disabled,
  onChange,
}: {
  options: AccountOptions;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const [search, setSearch] = useState('');
  const details = useRef<HTMLDetailsElement>(null);
  const groups = Object.keys(options.groups).filter(group =>
    `${group} ${options.groups[group]} ${options.group_ratios[group] ?? ''}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );
  return (
    <div className={styles.field}>
      <span id="tokenlat-group-label">{t('group')}</span>
      <details ref={details} className={styles.selector}>
        <summary
          aria-labelledby="tokenlat-group-label"
          aria-disabled={disabled}
          onClick={event => {
            if (disabled) {
              event.preventDefault();
            }
          }}
        >
          <GroupLabel value={value} options={options} />
        </summary>
        <div className={styles['selection-body']}>
          <input
            aria-label={t('search_groups')}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
            placeholder={t('search_groups')}
            value={search}
            onChange={event => setSearch(event.target.value)}
            disabled={disabled}
          />
          <div className={styles['option-list']}>
            {groups.map(group => (
              <label key={group} className={styles.option}>
                <input
                  type="radio"
                  name="tokenlat-group"
                  value={group}
                  checked={value === group}
                  disabled={disabled}
                  onChange={() => {
                    onChange(group);
                    setSearch('');
                    if (details.current) {
                      details.current.open = false;
                    }
                  }}
                />
                <GroupLabel value={group} options={options} />
              </label>
            ))}
            {!groups.length ? (
              <p className={styles.muted}>{t('no_matches')}</p>
            ) : null}
          </div>
        </div>
      </details>
    </div>
  );
}

export function ModelSelector({
  available,
  selected,
  disabled,
  onChange,
}: {
  available: string[];
  selected: string[];
  disabled: boolean;
  onChange: (value: string[]) => void;
}) {
  const [search, setSearch] = useState('');
  const filtered = available.filter(model =>
    model.toLowerCase().includes(search.trim().toLowerCase()),
  );
  return (
    <div className={styles.field}>
      <span id="tokenlat-model-label">{t('models')}</span>
      <details className={styles.selector}>
        <summary
          aria-labelledby="tokenlat-model-label"
          aria-disabled={disabled}
          onClick={event => {
            if (disabled) {
              event.preventDefault();
            }
          }}
        >
          {selected.length
            ? `${t('selected_models')}: ${selected.length}`
            : t('models_placeholder')}
        </summary>
        <div className={styles['selection-body']}>
          <input
            aria-label={t('search_models')}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
              }
            }}
            placeholder={t('search_models')}
            value={search}
            disabled={disabled}
            onChange={event => setSearch(event.target.value)}
          />
          <div className={styles['option-list']}>
            {filtered.map(model => (
              <label key={model} className={styles.option}>
                <input
                  type="checkbox"
                  checked={selected.includes(model)}
                  disabled={disabled}
                  onChange={event =>
                    onChange(
                      event.target.checked
                        ? [...selected, model]
                        : selected.filter(item => item !== model),
                    )
                  }
                />
                <span>{model}</span>
              </label>
            ))}
            {!filtered.length ? (
              <p className={styles.muted}>{t('no_matches')}</p>
            ) : null}
          </div>
        </div>
      </details>
      {selected.length ? (
        <div className={styles.chips}>
          {selected.map(model => (
            <button
              type="button"
              key={model}
              disabled={disabled}
              className={styles.chip}
              aria-label={`${t('remove_model')} ${model}`}
              onClick={() => onChange(selected.filter(item => item !== model))}
            >
              {model}
              <span aria-hidden>×</span>
            </button>
          ))}
          <button
            type="button"
            disabled={disabled}
            className={styles['text-button']}
            onClick={() => onChange([])}
          >
            {t('clear_models')}
          </button>
        </div>
      ) : null}
      <span className={styles.muted}>{t('models_hint')}</span>
    </div>
  );
}
